import apiClient from "@/axiosConfig";
import { useUxStore } from "@/stores/uxStore";

const getUxStore = () => useUxStore();

export default {
  getFeatures(companyId) {
    return apiClient.get(`/company-features/companies/${companyId}`);
  },
  updateFeature(companyId, featureId, enabled, expectedRevision) {
    return apiClient.put(`/company-features/companies/${companyId}/${featureId}`, {
      enabled, expectedRevision,
    });
  },
  async getAll(search = null, page = 1, pageSize = 200) {
    try {
      getUxStore().startLoading();
      const params = { page, pageSize };
      if (search) params.search = search;
      return await apiClient.get("/company", { params });
    } catch (error) {
      console.error("Error in CompanyService.getAll:", error);
      getUxStore().setError("Fehler beim Laden der Firmen.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async getById(id) {
    try {
      getUxStore().startLoading();
      return await apiClient.get(`/company/${id}`);
    } catch (error) {
      console.error("Error in CompanyService.getById:", error);
      getUxStore().setError("Fehler beim Laden der Firma.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async getApiQuotaPlans() {
    try {
      getUxStore().startLoading();
      return await apiClient.get("/company/api-quota-plans");
    } catch (error) {
      console.error("Error in CompanyService.getApiQuotaPlans:", error);
      getUxStore().setError("Fehler beim Laden der API-Pläne.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async getActivePendingDowngradePlan(companyId) {
    try {
      getUxStore().startLoading();
      return await apiClient.get(`/company/${companyId}/pending-downgrade-plan`);
    } catch (error) {
      console.error("Error in CompanyService.getActivePendingDowngradePlan:", error);
      getUxStore().setError("Fehler beim Laden des offenen API-Plan-Downgrades.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async previewApiPlanChange(companyId, requestedPlanCode) {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/company/${companyId}/api-plan-change/preview`, { requestedPlanCode });
    } catch (error) {
      console.error("Error in CompanyService.previewApiPlanChange:", error);
      getUxStore().setError("Fehler bei der API-Plan-Vorschau.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async applyApiPlanChange(companyId, requestedPlanCode) {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/company/${companyId}/api-plan-change`, { requestedPlanCode });
    } catch (error) {
      console.error("Error in CompanyService.applyApiPlanChange:", error);
      getUxStore().setError("Fehler beim Ändern des API-Plans.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async getDefault() {
    try {
      getUxStore().startLoading();
      return await apiClient.get("/company/default");
    } catch (error) {
      console.error("Error in CompanyService.getDefault:", error);
      if (error?.response?.status === 404) {
        return { data: null };
      }
      getUxStore().setError("Fehler beim Laden der Standard-Firma.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async create(payload) {
    try {
      getUxStore().startLoading();
      return await apiClient.post("/company", payload);
    } catch (error) {
      console.error("Error in CompanyService.create:", error);
      getUxStore().setError(error?.response?.data || "Fehler beim Erstellen der Firma.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async update(id, payload) {
    try {
      getUxStore().startLoading();
      return await apiClient.put(`/company/${id}`, payload);
    } catch (error) {
      console.error("Error in CompanyService.update:", error);
      getUxStore().setError(error?.response?.data || "Fehler beim Aktualisieren der Firma.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async remove(id) {
    try {
      getUxStore().startLoading();
      return await apiClient.delete(`/company/${id}`);
    } catch (error) {
      console.error("Error in CompanyService.remove:", error);
      getUxStore().setError("Fehler beim Löschen der Firma.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async setDefault(id) {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/company/${id}/default`);
    } catch (error) {
      console.error("Error in CompanyService.setDefault:", error);
      getUxStore().setError("Fehler beim Setzen der Standard-Firma.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async getPaymentTerms(companyId) {
    try {
      getUxStore().startLoading();
      return await apiClient.get(`/company/${companyId}/paymentterms`);
    } catch (error) {
      console.error("Error in CompanyService.getPaymentTerms:", error);
      getUxStore().setError("Fehler beim Laden der Zahlungsbedingungen.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  exportPaymentTerms(companyId) {
    return apiClient.get(`/company/${companyId}/paymentterms/export`, { responseType: "blob" });
  },
  previewPaymentTermImport(companyId, input) {
    return apiClient.post(`/company/${companyId}/paymentterms/import/preview`, input);
  },
  importPaymentTerms(companyId, input) {
    return apiClient.post(`/company/${companyId}/paymentterms/import`, input);
  },

  async createPaymentTerm(companyId, payload) {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/company/${companyId}/paymentterms`, payload);
    } catch (error) {
      console.error("Error in CompanyService.createPaymentTerm:", error);
      getUxStore().setError("Fehler beim Erstellen der Zahlungsbedingung.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async updatePaymentTerm(companyId, paymentTermId, payload) {
    try {
      getUxStore().startLoading();
      return await apiClient.put(`/company/${companyId}/paymentterms/${paymentTermId}`, payload);
    } catch (error) {
      console.error("Error in CompanyService.updatePaymentTerm:", error);
      getUxStore().setError("Fehler beim Aktualisieren der Zahlungsbedingung.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async removePaymentTerm(companyId, paymentTermId) {
    try {
      getUxStore().startLoading();
      return await apiClient.delete(`/company/${companyId}/paymentterms/${paymentTermId}`);
    } catch (error) {
      console.error("Error in CompanyService.removePaymentTerm:", error);
      getUxStore().setError("Fehler beim Löschen der Zahlungsbedingung.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async setDefaultPaymentTerm(companyId, paymentTermId) {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/company/${companyId}/paymentterms/${paymentTermId}/default`);
    } catch (error) {
      console.error("Error in CompanyService.setDefaultPaymentTerm:", error);
      getUxStore().setError("Fehler beim Setzen der Standard-Zahlungsbedingung.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async export(format = "json") {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/company/export?format=${encodeURIComponent(format)}`);
    } catch (error) {
      console.error("Error in CompanyService.export:", error);
      getUxStore().setError("Fehler beim Export der Firmen.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async import(fileId, upsert = true) {
    try {
      getUxStore().startLoading();
      return await apiClient.post("/company/import", { fileId, upsert });
    } catch (error) {
      console.error("Error in CompanyService.import:", error);
      getUxStore().setError("Fehler beim Import der Firmen.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },
};
